// Media Upload Client for Admin Panel
// Handles image uploads, management, and Cloudinary integration

import { authFetch } from '../auth/client';

// ============================================
// MEDIA API
// ============================================

// Get all media with pagination
export async function getMedia(options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.set('page', options.page);
  if (options.limit) params.set('limit', options.limit);

  const query = params.toString();
  return authFetch(`/admin/media${query ? `?${query}` : ''}`);
}

// Get single media item
export async function getMediaItem(id) {
  return authFetch(`/admin/media?id=${id}`);
}

// Upload media (base64 or URL)
export async function uploadMedia(data) {
  return authFetch('/admin/media', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// Delete media
export async function deleteMedia(id) {
  return authFetch(`/admin/media?id=${id}`, {
    method: 'DELETE'
  });
}

// Get upload signature for direct browser upload
export async function getUploadSignature(folder = 'amped-therapeutics/blog') {
  return authFetch(`/admin/media?action=signature&folder=${encodeURIComponent(folder)}`);
}

// ============================================
// FILE HELPERS
// ============================================

// Convert file to base64
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

// Upload file (convenience wrapper)
export async function uploadFile(file, options = {}) {
  const base64 = await fileToBase64(file);
  return uploadMedia({
    image: base64,
    filename: file.name,
    altText: options.altText || file.name.replace(/\.[^/.]+$/, ''),
    folder: options.folder
  });
}

// Upload from URL
export async function uploadFromUrl(url, options = {}) {
  return uploadMedia({
    image: url,
    filename: options.filename || url.split('/').pop(),
    altText: options.altText,
    folder: options.folder
  });
}

// ============================================
// DIRECT CLOUDINARY UPLOAD (Browser-side)
// ============================================

// Upload directly to Cloudinary (bypasses server for large files)
export async function uploadDirectToCloudinary(file, options = {}) {
  // Get signature from server
  const { signature, timestamp, apiKey, cloudName, folder } = await getUploadSignature(
    options.folder
  );

  // Build form data
  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', apiKey);
  formData.append('timestamp', timestamp);
  formData.append('signature', signature);
  formData.append('folder', folder);

  if (options.publicId) {
    formData.append('public_id', options.publicId);
  }

  // Upload to Cloudinary
  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    {
      method: 'POST',
      body: formData
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Upload failed');
  }

  const result = await response.json();

  // Optionally save to our database
  if (options.saveToDatabase !== false) {
    await uploadMedia({
      image: result.secure_url,
      filename: file.name,
      altText: options.altText || file.name.replace(/\.[^/.]+$/, ''),
      folder
    });
  }

  return {
    success: true,
    publicId: result.public_id,
    url: result.secure_url,
    width: result.width,
    height: result.height,
    format: result.format,
    bytes: result.bytes
  };
}

// ============================================
// IMAGE UTILITIES
// ============================================

// Validate file is an image
export function isValidImage(file) {
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  return validTypes.includes(file.type);
}

// Check file size (default max 10MB)
export function isValidFileSize(file, maxSizeMB = 10) {
  return file.size <= maxSizeMB * 1024 * 1024;
}

// Generate Cloudinary URL with transformations
export function getCloudinaryUrl(publicId, options = {}) {
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'dhfomdejv';
  const transformations = [];

  if (options.width) transformations.push(`w_${options.width}`);
  if (options.height) transformations.push(`h_${options.height}`);
  if (options.crop) transformations.push(`c_${options.crop}`);
  if (options.quality) transformations.push(`q_${options.quality}`);
  if (options.format) transformations.push(`f_${options.format}`);

  // Default optimizations
  if (!options.quality) transformations.push('q_auto');
  if (!options.format) transformations.push('f_auto');

  const transformString = transformations.length > 0
    ? transformations.join(',') + '/'
    : '';

  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformString}${publicId}`;
}

// Generate thumbnail URL
export function getThumbnailUrl(publicId, size = 200) {
  return getCloudinaryUrl(publicId, {
    width: size,
    height: size,
    crop: 'fill'
  });
}

// Generate responsive srcset
export function getSrcSet(publicId, widths = [400, 800, 1200, 1600]) {
  return widths.map(w => {
    const url = getCloudinaryUrl(publicId, { width: w });
    return `${url} ${w}w`;
  }).join(', ');
}

// Get optimized image props for <img> tag
export function getOptimizedImageProps(publicId, options = {}) {
  const {
    width = 800,
    alt = '',
    className = '',
    sizes = '(max-width: 600px) 100vw, 800px'
  } = options;

  return {
    src: getCloudinaryUrl(publicId, { width }),
    srcSet: getSrcSet(publicId),
    sizes,
    alt,
    className,
    loading: 'lazy'
  };
}

// ============================================
// DRAG & DROP HELPERS
// ============================================

// Handle drag over event
export function handleDragOver(e) {
  e.preventDefault();
  e.stopPropagation();
}

// Handle drop event - returns files array
export function handleDrop(e) {
  e.preventDefault();
  e.stopPropagation();

  const files = [];
  if (e.dataTransfer.items) {
    for (let i = 0; i < e.dataTransfer.items.length; i++) {
      if (e.dataTransfer.items[i].kind === 'file') {
        const file = e.dataTransfer.items[i].getAsFile();
        if (isValidImage(file)) {
          files.push(file);
        }
      }
    }
  } else {
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      if (isValidImage(e.dataTransfer.files[i])) {
        files.push(e.dataTransfer.files[i]);
      }
    }
  }

  return files;
}

// Handle paste event - returns files array
export function handlePaste(e) {
  const files = [];
  const items = e.clipboardData?.items;

  if (items) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
  }

  return files;
}
